/**
 * Undo and "Not done after all" on the web (core corrections.ts, docs/DESIGN.md §3b), pure so it's tested without a
 * browser (apps/web/test/checkoff.test.ts). Screens use components/Checkoff.tsx.
 *  - A saved check-off is either on the server (a CheckoffRef) or still in this browser's outbox (a job id).
 *  - Undo: the server removes it (the same account, 30 s), or the queued job is dropped and never sent.
 *  - A refused Undo (too late, or not yours) says it's on the record; the row offers "Not done after all".
 */
import { UNDO_SECONDS, canRevert, checkoffRef, undoOnScreen, type CheckoffKind, type CheckoffRef, type Correction, type Role } from "@logwave/core";
import { ApiError, type Api, type BizCheckoffKind } from "../api.ts";
import { saveErrorText } from "./failures.ts";
import { localDate } from "./time.ts";

/** Where a check-off went: the server (`home`: a home chore, /homes routes) or this browser's outbox. */
export type Saved = { ref: CheckoffRef; orgId: string } | { jobId: string };
export type UndoOutcome = { ok: true } | { ok: false; revert: boolean; message: string };

/** How long the toast offers Undo. */
export const UNDO_MS = UNDO_SECONDS * 1000;

/** "Done ✓ Wipe the bar" — the one toast every check-off shows. */
export const doneText = (what: string) => `Done ✓ ${what}`;

/** The saved answer of a check-off as something Undo can act on. */
export function savedOf(kind: CheckoffKind, orgId: string, r: { id: string; at: string } | null | undefined, jobId?: string): Saved | null {
  if (r) return { ref: checkoffRef(kind, r.id, r.at), orgId };
  return jobId ? { jobId } : null;
}

export async function undoSaved(saved: Saved, deps: { api: Pick<Api, "undoCheckoff" | "undoChore">; drop?: (jobId: string) => boolean }): Promise<UndoOutcome> {
  if ("jobId" in saved) {
    if (deps.drop?.(saved.jobId)) return { ok: true };
    return { ok: false, revert: true, message: "It already sent. It's on the record now; use \"Not done after all\" to correct it." };
  }
  try {
    if (saved.ref.kind === "chore") await deps.api.undoChore(saved.orgId, saved.ref.id);
    else await deps.api.undoCheckoff(saved.orgId, saved.ref.kind as BizCheckoffKind, saved.ref.id);
    return { ok: true };
  } catch (e) {
    if (e instanceof ApiError && (e.reason === "revert" || e.status === 409 || e.status === 403)) return { ok: false, revert: e.reason === "revert", message: e.message };
    return { ok: false, revert: false, message: saveErrorText(e) };
  }
}

/**
 * Show "Not done after all" on a done row? The same rule the server applies (core canRevert: the doer the same day in
 * the place's time zone, a manager or owner any time within retention), and never while the Undo toast still is.
 */
export function revertVisible(item: { by: string; at: string; correction?: Correction | null }, me: { userId: string; role: Role }, tz: string | undefined, now: Date = new Date()): boolean {
  if (item.correction || undoOnScreen(item, now.getTime())) return false;
  const sameDay = localDate(new Date(item.at), tz) === localDate(now, tz);
  return canRevert(item, { userId: me.userId, role: me.role, now, sameDay });
}
